import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';
import { changeLanguage } from '../../store/actions/localization/localizationAction';
import { Localizations } from '../../localization/definitions/enums';
import { SUSY_LOCALSTORAGE_UILANGUAGE_KEY } from '../../localization/definitions/constants';
import { EnglishFlag, GermanFlag } from '../shared/icons/icons';
import { AppSelect } from '../shared/Select';
import './LanguageSwitcher.css';

const LanguageSwitcher = () => {
    const localization = useSelector((state: RootState) => state.localization);
    const languageSwitcherLocalization = useSelector(
        (state: RootState) =>
            state.localization.localizationStrings.susy.localization
                .languageSwitcher
    );

    const dispatch = useDispatch();

    useEffect(() => {
        const lastLanguage = localStorage.getItem(
            SUSY_LOCALSTORAGE_UILANGUAGE_KEY
        ) as Localizations;

        if (!lastLanguage) {
            return;
        }

        dispatch(changeLanguage(lastLanguage));
    }, []);

    const changeLanguageClick = (language: Localizations) => {
        if (localization.currentLanguage === language) {
            return;
        }

        dispatch(changeLanguage(language));
        localStorage.setItem(SUSY_LOCALSTORAGE_UILANGUAGE_KEY, language);
    };

    const getFlagForLabel = (languageCode: string) => {
        switch (languageCode) {
            case Localizations.en:
                return <EnglishFlag />;
            case Localizations.de:
                return <GermanFlag />;
            default:
                return <EnglishFlag />;
        }
    };

    const getLabelForOption = (languageCode: string) => {
        return (
            <div data-testid={'option-' + languageCode}>
                <span className={'ml-2'}>
                    <input
                        type='radio'
                        name='language'
                        value='language'
                        id={`radio-language-${languageCode}`}
                        className='susy-radio'
                        checked={languageCode === localization.currentLanguage}
                        data-testid='radiobtn-language'
                    />
                    <label htmlFor={`radio-language-${languageCode}`} />

                    {getFlagForLabel(languageCode)}
                    <span className='ml-1'>
                        {getLanguageNameForLabel(languageCode)}
                    </span>
                </span>
            </div>
        );
    };

    const getLanguageNameForLabel = (languageCode: string) => {
        switch (languageCode) {
            case Localizations.en:
                return languageSwitcherLocalization.english;
            case Localizations.de:
                return languageSwitcherLocalization.deutsch;
            default:
                return languageSwitcherLocalization.english;
        }
    };

    const getCurrentValue = () => {
        return {
            value: localization.currentLanguage,
            label: getFlagForLabel(localization.currentLanguage),
        };
    };

    const options = Object.values(Localizations).map((language) => ({
        value: language,
        label: getLabelForOption(language),
    }));

    return (
        <AppSelect
            testId={'language-switcher'}
            classes={'language-switcher'}
            name={'language-switcher'}
            options={options}
            value={getCurrentValue()}
            onChange={(language) => changeLanguageClick(language.value)}
        />
    );
};

export default LanguageSwitcher;
